import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { EDSDataGridContainer } from '@gehc-eds-react/eds-datagrid-container';
import { EDSDataGrid } from '@gehc-eds-react/eds-datagrid';
import { EDSBadge } from '@gehc-eds-react/eds-badge';
import { EDSButton } from '@gehc-eds-react/eds-button';
import { EDSIcon } from '@gehc-eds-react/eds-icon';
import { EDSPagination } from '@gehc-eds-react/eds-pagination';
import { 
    navigateToFolderWithPagination,
    clearDatasetContent, 
    resetToRoot
} from '../../redux/slices/datasetContentSlice';
import DataViewer from './DataViewer';
import logger from '../../utils/logger';
import { formatFileSize } from '../../utils/formatUtils';
import { getStatusBadgeVariant, getStatusDisplayName } from '../../constants/uploadStatus';
import BreadcrumbNav from '../../components/BreadCrumbNav/BreadcrumbNav';

const isNavigable = (type) => type === 'folder' || type === 'zip';

const DataUploadChild = ({ data, onBack }) => {
    const dispatch = useAppDispatch();
    const { 
        contents, 
        breadcrumb, 
        currentPath, 
        currentPage,
        totalPagesAtLevel,
        totalItemsAtLevel,
        hasNextPage,
        hasPrevPage,
        totalFilesInDataset,
        isLoading, 
        error
    } = useAppSelector((state) => state.datasetContent);
    
    const [selectedFile, setSelectedFile] = useState(null);
    const timeoutRef = useRef(null);

    const datasetId = data?.originalData?.DatasetID || data?.uid;
    const datasetName = data?.name || 'Unknown Dataset';

    
    // ✅ Load root level on component mount
    useEffect(() => {
        if (datasetId) {
            logger.debug('🔄 Loading dataset root:', { datasetId, datasetName });
            
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            
            timeoutRef.current = setTimeout(() => {
                dispatch(resetToRoot());
                dispatch(navigateToFolderWithPagination({ 
                    datasetId, 
                    path: '/', 
                    page: 1 
                }));
            }, 100);
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            dispatch(clearDatasetContent());
        };
    }, [datasetId, dispatch]);
   
     /***
    This effect sets up search event listeners for the EDS data grid:
    - Listens for 'eds-search-input' and 'eds-search-clear' events.
    - On search input:
        - Debounces API calls (800ms) to avoid rapid requests while typing.
        - Updates the search term state.
        - If the search is empty, immediately calls the API for all data.
        - If not empty, waits 800ms then calls the API with the search term.
        - Shows a loading indicator while the API call is in progress.
    - On search clear:
        - Resets the search term.
        - Cancels any pending debounce.
        - Calls the API for all data and shows loading.
    - Cleans up:
        - Removes event listeners on unmount.
        - Clears any pending debounce timeout.
    ***/
   
   
   
    useEffect(() => {
        let searchDebounceTimeout = null;

        const handleSearchInput = (e) => {
            // Debug: log the full event detail to see what EDS sends
            logger.debug('🔍 EDS search event detail:', JSON.stringify(e.detail));
            
            const term = e.detail?.searchTerm ?? e.detail?.value ?? e.detail ?? '';
            const searchValue = typeof term === 'string' ? term : '';
            
            if (searchDebounceTimeout) clearTimeout(searchDebounceTimeout);
            
            if (searchValue.trim() === '') {
                dispatch(navigateToFolderWithPagination({
                    datasetId, path: currentPath, page: 1, search: ''
                }));
            } else {
                searchDebounceTimeout = setTimeout(() => {
                    logger.debug('🔍 Sending search to API:', searchValue.trim());
                    dispatch(navigateToFolderWithPagination({
                        datasetId, path: currentPath, page: 1, search: searchValue
                    }));
                }, 2000);
            }
        };

        const handleSearchClear = () => {
        logger.debug('🔍 EDS search cleared via X button');
        if (searchDebounceTimeout) clearTimeout(searchDebounceTimeout);
        dispatch(navigateToFolderWithPagination({
            datasetId, path: currentPath, page: 1, search: ''
        }))
    };

        window.addEventListener('eds-search-input', handleSearchInput);
        window.addEventListener('eds-search-clear', handleSearchClear);

        return () => {
            window.removeEventListener('eds-search-input', handleSearchInput);
            window.removeEventListener('eds-search-clear', handleSearchClear);
            if (searchDebounceTimeout) clearTimeout(searchDebounceTimeout);
        };
    }, [dispatch, datasetId, currentPath]);
    // ✅ Updated handleItemClick - only folders clickable
    const handleItemClick = useCallback((row) => {
         if (isNavigable(row.type)) {   
            const newPath = currentPath === '/' 
                ? `/${row.originalItem.name}` 
                : `${currentPath}/${row.originalItem.name}`;
            
            logger.debug('📁 Navigating to folder:', { 
                folderName: row.originalItem.name, 
                newPath 
            });
            
            dispatch(navigateToFolderWithPagination({ 
                datasetId, 
                path: newPath, 
                page: 1
            }));
        } else {
            // ✅ Files are not clickable for navigation
            // Could show file details in a modal or sidebar instead
            logger.debug('📄 File clicked (no navigation):', row.originalItem.name);
        }
    }, [dispatch, datasetId, currentPath]);

    // ✅ Handle pagination within current folder
    const handlePageChange = useCallback((newPage) => {
        logger.debug(`📖 Changing to page ${newPage} in folder ${currentPath}`);
        
        // Call API for new page at current level
        dispatch(navigateToFolderWithPagination({ 
            datasetId, 
            path: currentPath, 
            page: newPage
        }));
    }, [dispatch, datasetId, currentPath]);

    const handleNavigateToPath = useCallback((path) => {
    logger.debug('🍞 Navigating to path:', path);
    dispatch(navigateToFolderWithPagination({ 
        datasetId, 
        path, 
        page: 1 
        }));
    }, [dispatch, datasetId]);


    // ✅ Transform data for EDS DataGrid
    const dataList = useMemo(() => {
        if (!contents || !Array.isArray(contents)) return [];
        
        return contents.map((item, index) => ({
            uid: index + 1,
            name: item.name,
            dataType: getDataType(item),
            dataSize: formatFileSize(item.size || 0),  // ✅ Handle size: 0
            date: new Date(item.lastModified).toLocaleDateString('en-US'),
            status: item.status,
            uploadProgress: item.uploadProgress || 0,
            type: item.type,
            originalItem: item
        }));
    }, [contents]);

    // ✅ Data columns
    const dataColumn = {
        "uid": {"displayName": "Id", "isPrimary": true, "isHidden": true},
        "name": {
            "displayName": "Data",
            renderCellValue: (name, row) => {
                const navigable = isNavigable(row.type);  
                return (
                <span
                    className={navigable ? 'menu-link' : 'file-name'}
                    onClick={navigable ? () => handleItemClick(row) : undefined}
                    title={name} // Show full name on hover
                    style={{
                        cursor: navigable ? "pointer" : "default",
                        color: navigable ? "#7252BC" : "inherit",
                        textDecoration: row.type === 'folder' ? "underline" : "none",
                        display: "block",
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        wordBreak: "break-word"
                    }}
                >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', lineHeight: 'normal' }}>
                        <EDSIcon
                            iconName={row.type === 'zip' ? 'ico-exam-archive-24' : row.type === 'folder' ? 'ico-folder-24' : 'ico-document-24'}
                        ></EDSIcon>
                        <span style={{ verticalAlign: 'middle' }}>{name}</span>
                    </span>
                </span>
            );
        }
        },
        "dataType": {
            "displayName": "Type",
            renderCellValue: (dataType) => (
                <EDSBadge
                    variant={'accent-gray'}  
                    value={dataType} 
                    label={""}
                    size={"medium"}
                />
            )
        },
        "dataSize": {"displayName": "Size"},
        "date": {"displayName": "Modified"},
        "status": {
            "displayName": "Status",
            renderCellValue: (status, row) => (
                <EDSBadge
                    variant={getStatusBadgeVariant(status)}
                    value={getStatusDisplayName(status)} 
                    label={""}
                    size={"medium"}
                />
            )
        }
    };


    const dataGridKey = useMemo(() => {
        return `${datasetId}-${currentPath}-${currentPage}`;
    }, [datasetId, currentPath, currentPage]);


    // File viewer
    if (selectedFile) {
        return (
            <DataViewer 
       selectedFile={selectedFile} 
                parentData={data}
                onBackToChild={() => setSelectedFile(null)}
                onBackToMain={onBack}
            />
        );
    }

    // Error state
    if (error) {
        return (
            <div className="data-upload-container">
                <div className="upload-data-header fill--1">
                    <BreadcrumbNav
                    datasetName={datasetName}
                    breadcrumb={breadcrumb}
                    currentPath={currentPath}
                    onNavigateBack={onBack}
                    onNavigateToPath={handleNavigateToPath}
                />
                    
                </div>


                <div className="projects-container fill--1">
                    <div style={{ textAlign: 'center', padding: '60px' }}>
                        <span style={{ color: 'red' }}>Error: {error}</span>
                        <br />
                        <button onClick={() => dispatch(navigateToFolderWithPagination({ 
                            datasetId, 
                            path: currentPath, 
                            page: currentPage 
                        }))}>
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

 return (  
    <div className="data-upload-container">
        <div className="upload-data-header fill--1" style={{ justifyContent:'flex-start' }}>
            <BreadcrumbNav
                datasetName={datasetName}
                breadcrumb={breadcrumb}
                currentPath={currentPath}
                onNavigateBack={onBack}
                onNavigateToPath={handleNavigateToPath}
            />
            <div style={{ fontSize: '12px', color: '#999', textAlign: 'right', flexShrink: 0, whiteSpace: 'nowrap', marginLeft: '16px' }}>
                <div>{totalItemsAtLevel || 0} items in folder • Page {currentPage || 1} of {totalPagesAtLevel || 1}</div>
                <div style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
                    <EDSIcon iconName="ico-bargraph-16"></EDSIcon>
                    {totalFilesInDataset || 0} files total in dataset
                </div>
            </div>
        </div>


        
        <div className="projects-container fill--1">
            <div>
                <EDSDataGridContainer utilityFeatures="search" searchType="server">
                    <EDSDataGrid
                        key={dataGridKey}
                        activeRowId={0}
                        columnConfig={dataColumn}
                        columnFreezing={""}
                        disabledRowCells={{}}
                        gridFeatures={["row-hover","table-flex-horizontal"]}
                        isLoading={isLoading}
                        loaderType={"activity-indicator-linear"}
                                        labels={{"rowText":"rows of","multiSelectText":"{{rowCount}} item(s) are selected","rowsPerPageText":"Rows per page",
                            "noRowsFoundText": dataList.length >= 10 ? "No Rows Found" : "",
                            "noFilterResultsText":"No results found",
                            "emptyGridMessage": dataList.length >= 10 ? "No data to display" : "",
                            "emptyGridSecondaryText":"",
                            "noFilterResultsSecondaryText":"Try adjusting your search or filter options to find the information you need.",
                            "rowsSelected":"row(s) selected",
                            "rowAddPlaceholderText":"Add text",
                            "rowEditPlaceholderText":"Edit row",
                            "rowAddModalHeaderText":"Add row",
                            "rowEditModalHeaderText":"Edit row",
                            "rowDeleteModalHeaderText":"Delete row",
                            "rowAddModalAcceptButtonText":"Add row",
                            "rowAddModalCancelButtonText":"Cancel",
                            "loadMore":"Load more",
                            "rowEditModalAcceptButtonText":"Save",
                            "rowEditModalCancelButtonText":"Cancel",
                            "rowDeleteModalAcceptButtonText":"Delete",
                            "rowDeleteModalCancelButtonText":"Cancel"
                        }}
                        loadedRowIds={[]}
                        rowFormat={{"size":"standard","style":"divider"}}
                        rows={dataList}
                        visibleRowCount={Math.min(10, dataList.length)}
                        totalRowCount={dataList.length}
                        modalWidth={"382px"}
                        sortType={""}
                        sort={[]}
                        utilityButtonVariant={"tertiary"}
                    />
                </EDSDataGridContainer>
                {/* ✅ FIXED: Built-in DataGrid search  */}

            </div>

            {/* EDS Pagination — shows when more than 1 page */}
            {(totalPagesAtLevel || 0) > 1 && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 0',
                    marginTop: '8px'
                }}>
                    <div style={{ fontSize: '13px', color: '#666' }}>
                        {(() => {
                            const start = ((currentPage || 1) - 1) * 100 + 1;
                            const end = Math.min((currentPage || 1) * 100, totalItemsAtLevel || 0);
                            return `${start}-${end} of ${totalItemsAtLevel} items`;
                        })()}
                    </div>
                    <EDSPagination
                        totalRecords={totalItemsAtLevel || 0}
                        pageSizeOptions={{
                            value: 100,
                            options: [],
                            label: ""
                        }}
                        currentPage={currentPage || 1}
                        onEDSPaginationPageNumberClick={(e) => {
                            const newPage = e.detail?.currentPage || e.detail?.value || e.detail;
                            if (newPage && Number(newPage) !== currentPage) {
                                handlePageChange(Number(newPage));
                            }
                        }}
                        onEDSPaginationPageNextClick={() => handlePageChange((currentPage || 1) + 1)}
                        onEDSPaginationPagePreviousClick={() => handlePageChange((currentPage || 1) - 1)}
                    />
                </div>
            )}
        </div>
    </div>
);
};
const getDataType = (item) => {
    if (item.type === 'folder') {
        return 'Folder';
    }

    if (item.type === 'zip') {          
        return 'Zip';
    }
  // Use the extension field from the API response
    const extension = item.extension;
    
    if (extension) {
        // Special case: dcm → DICOM for readability
        if (extension.toLowerCase() === 'dcm') {
            return 'DICOM';
        }
        return extension.toUpperCase();
    }
    
    // Fallback: extract from filename if extension field is missing
    const fileName = item.name || '';
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex > 0) {
        const ext = fileName.substring(lastDotIndex + 1).toLowerCase();
        if (ext === 'dcm') return 'DICOM';
        return ext.toUpperCase();
    }
    
    return 'Unknown';
};

export default DataUploadChild;
